import React from "react";
import { Table, Tag, Button, Tooltip, Space } from "antd";
import {
  EyeOutlined,
  SendOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  StopOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const RequisitionTable = ({
  requisitions,
  loading,
  onViewDetails,
  onSubmit,
  onHodApprove,
  onFinanceApprove,
  onMarkFulfilled,
  onCancel,
  onDelete,
}) => {
  const getStatusColor = (status) => {
    const statusColors = {
      Draft: "default",
      Submitted: "blue",
      "HoD Approved": "cyan",
      "HoD Rejected": "red",
      "Finance Approved": "purple",
      "Finance Rejected": "red",
      Procurement: "orange",
      Fulfilled: "green",
      Cancelled: "gray",
    };
    return statusColors[status] || "default";
  };

  const getPriorityColor = (priority) => {
    const colors = {
      Low: "default",
      Medium: "blue",
      High: "orange",
      Urgent: "red",
    };
    return colors[priority] || "default";
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const columns = [
    {
      title: "Requisition #",
      dataIndex: "requisitionNumber",
      key: "requisitionNumber",
      width: 150,
      render: (number, record) => (
        <a onClick={() => onViewDetails(record)}>{number}</a>
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Item",
      dataIndex: "departmentName",
      key: "departmentName",
      width: 150,
    },
    {
      title: "Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 130,
      align: "right",
      render: (amount) => (
        <span style={{ fontWeight: 600 }}>{formatCurrency(amount)}</span>
      ),
      sorter: (a, b) =>
        parseFloat(a.totalAmount || 0) - parseFloat(b.totalAmount || 0),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 100,
      render: (priority) => (
        <Tag color={getPriorityColor(priority)}>{priority}</Tag>
      ),
      filters: [
        { text: "Low", value: "Low" },
        { text: "Medium", value: "Medium" },
        { text: "High", value: "High" },
        { text: "Urgent", value: "Urgent" },
      ],
      onFilter: (value, record) => record.priority === value,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    {
      title: "Requested By",
      dataIndex: "requestedByName",
      key: "requestedByName",
      width: 140,
    },
    {
      title: "Needed By",
      dataIndex: "neededByDate",
      key: "neededByDate",
      width: 110,
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "-"),
      sorter: (a, b) => new Date(a.neededByDate) - new Date(b.neededByDate),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 110,
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "-"),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      defaultSortOrder: "descend",
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => onViewDetails(record)}
            />
          </Tooltip>
          {record.status === "Draft" && (
            <>
              <Tooltip title="Submit">
                <Button
                  type="link"
                  icon={<SendOutlined />}
                  onClick={() => onSubmit(record)}
                />
              </Tooltip>
              <Tooltip title="Delete">
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => onDelete(record)}
                />
              </Tooltip>
            </>
          )}
          {record.status === "Submitted" && (
            <>
              <Tooltip title="HoD Approve">
                <Button
                  type="link"
                  icon={<CheckCircleOutlined />}
                  style={{ color: "#52c41a" }}
                  onClick={() => onHodApprove(record, "approve")}
                />
              </Tooltip>
              <Tooltip title="HoD Reject">
                <Button
                  type="link"
                  icon={<CloseCircleOutlined />}
                  danger
                  onClick={() => onHodApprove(record, "reject")}
                />
              </Tooltip>
            </>
          )}
          {record.status === "HoD Approved" && (
            <>
              <Tooltip title="Finance Approve">
                <Button
                  type="link"
                  icon={<CheckCircleOutlined />}
                  style={{ color: "#722ed1" }}
                  onClick={() => onFinanceApprove(record, "approve")}
                />
              </Tooltip>
              <Tooltip title="Finance Reject">
                <Button
                  type="link"
                  icon={<CloseCircleOutlined />}
                  danger
                  onClick={() => onFinanceApprove(record, "reject")}
                />
              </Tooltip>
            </>
          )}
          {(record.status === "Finance Approved" ||
            record.status === "Procurement") && (
            <Tooltip title="Mark Fulfilled">
              <Button
                type="link"
                icon={<CheckCircleOutlined />}
                style={{ color: "#52c41a" }}
                onClick={() => onMarkFulfilled(record)}
              />
            </Tooltip>
          )}
          {!["Fulfilled", "Cancelled"].includes(record.status) &&
            record.status !== "Draft" && (
              <Tooltip title="Cancel">
                <Button
                  type="link"
                  icon={<StopOutlined />}
                  danger
                  onClick={() => onCancel(record)}
                />
              </Tooltip>
            )}
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={requisitions}
      rowKey="requisitionId"
      loading={loading}
      scroll={{ x: 1500 }}
      pagination={{
        pageSize: 10,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} requisitions`,
      }}
    />
  );
};

export default RequisitionTable;
